import React, { useEffect, useRef, useState } from "react";
import { Text } from "@vibe/core";
import SharedColumns from "./SharedColumns";
import type { ApplyOptimisticUpdate } from "./resynchronization.interface";

interface ExpandedRowProps {
  isOpen: boolean;
  syncId: string;
  dbItems: any[];
  rowData: any; // flags (no items/values) or a full scan result (has items[].column_values)
  filterQuery: string;
  applyOptimisticUpdate: ApplyOptimisticUpdate;
  fetchSyncItemValues: (syncId: string) => Promise<any>;
}

// A scan result already carries values; a cached flag row does not.
const hasValues = (rowData: any) =>
  Array.isArray(rowData?.items) &&
  rowData.items.length > 0 &&
  rowData.items[0]?.column_values !== undefined;

const ExpandedRow: React.FC<ExpandedRowProps> = ({
  isOpen,
  syncId,
  dbItems,
  rowData,
  filterQuery,
  applyOptimisticUpdate,
  fetchSyncItemValues,
}) => {
  const [fetched, setFetched] = useState<any>(null);
  // In-flight guard kept in a ref so it neither triggers a re-render nor sits in the
  // effect deps — `fetched` becoming truthy is the single stable terminal state.
  const inFlightRef = useRef(false);

  useEffect(() => {
    // An unverified row is never going to render SharedColumns (see below), so fetching its
    // values would be a round-trip to the very upstream that just failed, discarded on
    // arrival. An absent status is the older-backend case and must still behave as "scanned".
    if (!isOpen || hasValues(rowData) || fetched || inFlightRef.current || rowData?.status === "unverified") return;
    inFlightRef.current = true;
    fetchSyncItemValues(syncId)
      .then((result) => {
        setFetched(result);
      })
      .finally(() => {
        inFlightRef.current = false;
      });
  }, [isOpen, rowData, fetched, syncId, fetchSyncItemValues]);

  /**
   * Called after a resync POST succeeds, by SyncLatestModal via SharedColumns.
   *
   * This component owns the values the expanded grid renders — for a cache-seeded row
   * `fullData` is `fetched`, the state below, which applyOptimisticUpdate cannot reach: that
   * patches the PARENT's discrepancies array. So the badge and state pill flip immediately
   * while the two board values on screen keep showing the pre-resync difference, which reads
   * as "the resync did nothing".
   *
   * Clearing `fetched` re-arms the effect above (its guard is `|| fetched`), which re-requests
   * the values.
   *
   * Refetched immediately, with no settling delay, because there is no write still in flight to
   * race: hardResync awaits the real Monday column mutations — mutateColByStatus,
   * mutateColByDate, graphQlRequestEncryptedToken and the rest — before it answers, so Monday
   * has already acknowledged every write by the time this runs.
   *
   * `fullData` going null also flips SharedColumns' `isLoading`, so the grid shows its skeleton
   * rows while the values come back rather than displaying the stale ones as though current.
   *
   * A row that carries its own values needs none of this: hasValues(rowData) is true there,
   * `fullData` IS rowData, and patchDiscrepanciesAfterResync has already rewritten
   * items[].column_values. The effect's guard makes this a no-op on that path, which is right —
   * only the cache-seeded path has values the patch cannot reach.
   */
  const handleResynced = () => {
    setFetched(null);
  };

  const fullData = hasValues(rowData) ? rowData : fetched;
  const sharedColumns = rowData?.shared_columns ?? fullData?.shared_columns ?? [];

  return (
    <div style={{ height: isOpen ? "auto" : 0 }} className="overflow-hidden relative">
      <div className="overflow-x-auto">
        {/* Header row, board-name resolution and the loading skeleton all belong to
            DiscrepancyGrid now — this component is left with the fetch lifecycle only. */}
        {rowData?.status === "unverified" ? (
          // No grid at all: the engine could not reach every board this group spans, so no
          // count derived from partial data can be trusted here. An absent status falls
          // through to the SharedColumns branch below, so an older backend degrades to
          // today's behaviour rather than rendering an unknown state.
          <div style={{ padding: "var(--spacing-large)" }}>
            <Text type="text2" color="secondary">
              This item couldn&apos;t be fully scanned &mdash; some of its boards were
              unreachable or aren&apos;t visible to you, so its column values aren&apos;t shown.
              Treating a partial scan as a clean result could hide a real difference between
              boards.
            </Text>
          </div>
        ) : (
          <SharedColumns
            syncId={syncId}
            dbItems={dbItems}
            discrepancyItems={fullData?.items ?? []}
            sharedColumns={sharedColumns}
            totalDiscrepancies={rowData?.total_discrepancies}
            filter={filterQuery}
            applyOptimisticUpdate={applyOptimisticUpdate}
            onResynced={handleResynced}
            isLoading={isOpen && !fullData}
          />
        )}
      </div>
    </div>
  );
};

export default ExpandedRow;
